import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Dynamometers.css';
import { useProducts, type DynamometersSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const Dynamometers: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products: models, loading, error } = useProducts<DynamometersSpecs>('dynamometers', editMode);
  const navigate = useNavigate();


  const industrialApplications = [
    {
      industry: 'Industria Siderúrgica',
      applications: [
        'Pesaje de vigas de acero',
        'Control de bundles y paquetes',
        'Medición de rollos de acero',
        'Manejo de estructuras pesadas'
      ]
    },
    {
      industry: 'Servicios Públicos',
      applications: [
        'Tensión de líneas eléctricas',
        'Instalación de cables',
        'Mantenimiento de redes',
        'Trabajos de tracción'
      ]
    },
    {
      industry: 'Construcción',
      applications: [
        'Levantamiento de estructuras',
        'Control de cargas',
        'Verificación de tensiones',
        'Montaje de equipos'
      ]
    },
    {
      industry: 'Marítima',
      applications: [
        'Carga y descarga de buques',
        'Tensión de amarres',
        'Manejo de contenedores',
        'Operaciones portuarias'
      ]
    }
  ];

  const advantages = [
    {
      title: "Amplio Rango de Capacidades",
      description: "Desde 200 kg hasta 250,000 kg para cualquier aplicación industrial.",
    },
    {
      title: "Tecnología Avanzada",
      description: "Modelos electrónicos con display digital y control remoto opcional.",
    },
    {
      title: "Construcción Robusta",
      description: "Materiales de calidad aeronáutica y protección industrial.",
    },
    {
      title: "Versatilidad",
      description: "Opciones mecánicas y electrónicas para diferentes necesidades.",
    },
    {
      title: "Portabilidad",
      description: "Modelos compactos para aplicaciones móviles y de campo.",
    },
    {
      title: "Confiabilidad",
      description: "Diseño industrial probado para uso continuo en ambientes exigentes.",
    },
  ];

  const comparisonRows = [
    { feature: 'Capacidad Máxima', edxtreme: '250,000 kg', edjunior: '10,000 kg', ap: '50,000 kg' },
    { feature: 'Tipo de Display',  edxtreme: 'LCD Digital', edjunior: 'LCD Digital', ap: 'Mecánico / Analógico' },
    { feature: 'Alimentación',     edxtreme: 'Batería / AC', edjunior: '2 Baterías "C"', ap: 'Sin alimentación' },
    { feature: 'Control Remoto',   edxtreme: 'Opcional', edjunior: 'No', ap: 'No' },
    { feature: 'Protección',       edxtreme: 'NEMA 4X IP55', edjunior: 'Estándar', ap: 'Mecánica' },
    { feature: 'Portabilidad',     edxtreme: 'Limitada', edjunior: 'Alta', ap: 'Media' },
  ];

  return (
    <div className="dyn-page">
      {error && <div className="dyn-status-bar dyn-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="dyn-hero">
        <div className="container dyn-hero-content">
          <nav className="dyn-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="dyn-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="dyn-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Dinamómetros</span>
          </nav>
          <h1 className="dyn-hero-title">Dinamómetros</h1>
          <p className="dyn-hero-subtitle">
            Básculas colgantes para medición precisa de tensión, tracción y peso
            en aplicaciones industriales
          </p>
          {authType === 'internal' && (
            <button
              className={`dyn-btn-edit-mode${editMode ? ' dyn-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="dyn-hero-image" aria-hidden="true">
          <img src="/images/dynamometers.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="dyn-overview">
        <div className="container">
          <div className="dyn-overview-grid">
            <div className="dyn-overview-text">
              <span className="dyn-label">Descripción general</span>
              <h2>Medición de precisión en condiciones exigentes</h2>
              <p>
                Nuestros dinamómetros están diseñados para proporcionar mediciones precisas de peso,
                tensión y tracción en aplicaciones industriales exigentes. Ofrecemos tres líneas principales:
                EDxtreme para aplicaciones de alta capacidad, EDjunior para portabilidad, y AP mecánico
                para versatilidad y confiabilidad.
              </p>
            </div>
            <div className="dyn-overview-image">
              
            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="dyn-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Modelos</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/dynamometers')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : models.map((model, index) => (
                <ProductCard
                  key={index}
                  reviewed={model.reviewed}
                  endOfLife={model.endOfLife}
                  productId={model.productId}
                  name={model.name}
                  badge={model.specs?.type ?? undefined}
                  description={model.specs?.description ?? undefined}
                  image={model.image}
                  onRevisar={editMode && model.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(model.specs?.capacities?.length    ? [{ icon: '📦', label: 'Capacidades',    items: (model.specs?.capacities  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.units?.length         ? [{ icon: '📏', label: 'Unidades',        items: (model.specs?.units  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.features?.length      ? [{ icon: '⚙️', label: 'Características', items: (model.specs?.features  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.applications?.length  ? [{ icon: '🏭', label: 'Aplicaciones',   items: (model.specs?.applications  ?? []).filter((x): x is string => x != null)}] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Industrial applications ───────────────────────────── */}
      <section className="dyn-applications">
        <div className="container">
          <span className="dyn-label dyn-label--light">Industrias</span>
          <h2 className="dyn-section-title dyn-section-title--light">Aplicaciones por industria</h2>
          <div className="dyn-industries-grid">
            {industrialApplications.map((industry, index) => (
              <div key={index} className="dyn-industry-card">
                <h3>{industry.industry}</h3>
                <ul className="dyn-industry-list" aria-label={`Aplicaciones en ${industry.industry}`}>
                  {industry.applications.map((app, idx) => (
                    <li key={idx}>
                      <svg className="dyn-bullet-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {app}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Model comparison ─────────────────────────────────── */}
      <section className="dyn-comparison">
        <div className="container">
          <span className="dyn-label">Selección de modelo</span>
          <h2 className="dyn-section-title">Comparación de modelos</h2>
          <div className="dyn-comparison-wrap">
            <table className="dyn-comparison-table" aria-label="Comparación de modelos de dinamómetros">
              <thead>
                <tr>
                  <th scope="col">Característica</th>
                  <th scope="col">EDxtreme</th>
                  <th scope="col">EDjunior</th>
                  <th scope="col">AP Mecánico</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, index) => (
                  <tr key={index}>
                    <td className="dyn-comparison-feature">{row.feature}</td>
                    <td>{row.edxtreme}</td>
                    <td>{row.edjunior}</td>
                    <td>{row.ap}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Advantages ───────────────────────────────────────── */}
      <section className="dyn-advantages">
        <div className="container">
          <span className="dyn-label">Ventajas</span>
          <h2 className="dyn-section-title">Por qué elegir nuestros dinamómetros</h2>
          <div className="dyn-advantages-grid">
            {advantages.map((item, index) => (
              <div key={index} className="dyn-advantage-card">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dynamometers;
